/**
 * Generic Terms of Service Page for City Landing Pages
 *
 * Parameterized terms of service component that works for any city.
 * Updates city name, state, and contact info dynamically.
 *
 * Usage:
 *   <TermsOfServicePage
 *     cityName="Houston"
 *     stateName="Texas"
 *     cityDomain="houston.tx.toolboxlogic.com"
 *     effectiveDate="November 22, 2025"
 *   />
 */

import React from 'react';
import { useEffect } from 'react';

export interface TermsOfServicePageProps {
  cityName: string;
  stateName?: string;
  cityDomain: string; // e.g., "houston.tx.toolboxlogic.com"
  effectiveDate?: string; // Default: current date
  companyName?: string; // Default: "Capetian XX LLC dba ToolboxLogic"
  contactEmail?: string; // Default: "legal@toolboxlogic.com"
}

export function TermsOfServicePage({
  cityName,
  stateName = '',
  cityDomain,
  effectiveDate,
  companyName = 'Capetian XX LLC, a Nevada limited liability company doing business as ToolboxLogic',
  contactEmail = 'legal@toolboxlogic.com',
}: TermsOfServicePageProps) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const displayDate =
    effectiveDate ||
    new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

  const cityStateDisplay = stateName ? `${cityName}, ${stateName}` : cityName;

  return (
    <section className="py-12 sm:py-16 md:py-20 bg-muted/40">
      <div className="container max-w-3xl space-y-4 sm:space-y-6 px-4 sm:px-6">
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight">
          Terms of Service
        </h1>
        <p className="text-xs sm:text-sm text-muted-foreground">Effective {displayDate}</p>

        <p className="text-sm sm:text-base leading-relaxed">
          These Terms of Service ("Terms") govern your access to and use of the ToolboxLogic{' '}
          {cityName} pilot landing page, interest forms, and related communications (collectively,
          the "Services"). By using the Services, you agree to be bound by these Terms. If you do
          not agree, you must discontinue use immediately.
        </p>

        <div className="space-y-3 sm:space-y-4">
          <h2 className="text-xl sm:text-2xl font-semibold mt-6 sm:mt-8">1. Acceptance of Terms</h2>
          <p className="text-sm sm:text-base leading-relaxed">
            By accessing or using the Services, you represent that you are at least 18 years old and
            have the legal capacity to enter into these Terms. If you are using the Services on
            behalf of an organization, you represent that you have authority to bind that
            organization to these Terms.
          </p>
        </div>

        <div className="space-y-3 sm:space-y-4">
          <h2 className="text-xl sm:text-2xl font-semibold mt-6 sm:mt-8">
            2. Services Description
          </h2>
          <p className="text-sm sm:text-base leading-relaxed">
            The Services are a pilot landing page designed to gauge interest in a potential
            community engagement platform for {cityStateDisplay}. The Services allow you to:
          </p>
          <ul className="list-disc space-y-2 pl-5 sm:pl-6 text-sm sm:text-base">
            <li>Learn about the proposed platform and its features</li>
            <li>Submit an interest form to receive updates</li>
            <li>Apply to participate in a champions program (early tester/feedback group)</li>
            <li>Contact us with questions or feedback</li>
          </ul>
          <p className="text-sm sm:text-base leading-relaxed mt-3">
            <strong>Important:</strong> The Services do not constitute a commitment by ToolboxLogic
            to launch the platform in {cityName}. We are gathering community interest to determine
            feasibility.
          </p>
        </div>

        <div className="space-y-3 sm:space-y-4">
          <h2 className="text-xl sm:text-2xl font-semibold mt-6 sm:mt-8">3. User Conduct</h2>
          <p className="text-sm sm:text-base leading-relaxed">
            When using the Services, you agree to:
          </p>
          <ul className="list-disc space-y-2 pl-5 sm:pl-6 text-sm sm:text-base">
            <li>Provide accurate, truthful information in all forms</li>
            <li>Not impersonate any person or entity</li>
            <li>Not submit spam, malicious code, or harmful content</li>
            <li>Not use automated tools (bots, scrapers) to access the Services</li>
            <li>Not violate any applicable laws or regulations</li>
            <li>Not interfere with the proper functioning of the Services</li>
          </ul>
          <p className="text-sm sm:text-base leading-relaxed mt-3">
            We reserve the right to terminate access to the Services for any user who violates these
            Terms.
          </p>
        </div>

        <div className="space-y-3 sm:space-y-4">
          <h2 className="text-xl sm:text-2xl font-semibold mt-6 sm:mt-8">
            4. Intellectual Property
          </h2>
          <p className="text-sm sm:text-base leading-relaxed">
            All content on the Services, including text, graphics, logos, images, and software, is
            the property of {companyName} or its licensors and is protected by copyright, trademark,
            and other intellectual property laws.
          </p>
          <p className="text-sm sm:text-base leading-relaxed mt-3">
            You may not copy, modify, distribute, sell, or create derivative works from any content
            on the Services without our express written permission.
          </p>
        </div>

        <div className="space-y-3 sm:space-y-4">
          <h2 className="text-xl sm:text-2xl font-semibold mt-6 sm:mt-8">
            5. Disclaimer of Warranties
          </h2>
          <p className="text-sm sm:text-base leading-relaxed">
            THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND,
            EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF
            MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.
          </p>
          <p className="text-sm sm:text-base leading-relaxed mt-3">We do not warrant that:</p>
          <ul className="list-disc space-y-2 pl-5 sm:pl-6 text-sm sm:text-base">
            <li>The Services will be uninterrupted, error-free, or secure</li>
            <li>Any information provided through the Services is accurate or complete</li>
            <li>The platform described will ever launch in {cityName}</li>
          </ul>
        </div>

        <div className="space-y-3 sm:space-y-4">
          <h2 className="text-xl sm:text-2xl font-semibold mt-6 sm:mt-8">
            6. Limitation of Liability
          </h2>
          <p className="text-sm sm:text-base leading-relaxed">
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, {companyName.toUpperCase()} SHALL NOT BE LIABLE
            FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS
            OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA,
            USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM:
          </p>
          <ul className="list-disc space-y-2 pl-5 sm:pl-6 text-sm sm:text-base">
            <li>Your use or inability to use the Services</li>
            <li>Any unauthorized access to or use of our servers or personal information</li>
            <li>Any bugs, viruses, or malware transmitted through the Services</li>
            <li>
              Any errors or omissions in content, or any loss or damage incurred as a result of
              using content posted via the Services
            </li>
          </ul>
        </div>

        <div className="space-y-3 sm:space-y-4">
          <h2 className="text-xl sm:text-2xl font-semibold mt-6 sm:mt-8">7. Indemnification</h2>
          <p className="text-sm sm:text-base leading-relaxed">
            You agree to indemnify, defend, and hold harmless {companyName} and its officers,
            directors, employees, and agents from and against any claims, liabilities, damages,
            losses, and expenses, including reasonable attorneys' fees, arising out of or in any way
            connected with:
          </p>
          <ul className="list-disc space-y-2 pl-5 sm:pl-6 text-sm sm:text-base">
            <li>Your use of the Services</li>
            <li>Your violation of these Terms</li>
            <li>Your violation of any third-party rights</li>
          </ul>
        </div>

        <div className="space-y-3 sm:space-y-4">
          <h2 className="text-xl sm:text-2xl font-semibold mt-6 sm:mt-8">
            8. Governing Law and Dispute Resolution
          </h2>
          <p className="text-sm sm:text-base leading-relaxed">
            These Terms shall be governed by and construed in accordance with the laws of the State
            of Nevada, without regard to its conflict of law principles.
          </p>
          <p className="text-sm sm:text-base leading-relaxed mt-3">
            Any dispute arising out of or relating to these Terms or the Services shall be resolved
            through binding arbitration in accordance with the rules of the American Arbitration
            Association. The arbitration shall take place in Nevada, and judgment on the arbitration
            award may be entered in any court having jurisdiction.
          </p>
        </div>

        <div className="space-y-3 sm:space-y-4">
          <h2 className="text-xl sm:text-2xl font-semibold mt-6 sm:mt-8">9. Changes to Terms</h2>
          <p className="text-sm sm:text-base leading-relaxed">
            We reserve the right to modify these Terms at any time. Changes will be posted on this
            page with an updated "Effective Date." Your continued use of the Services after changes
            constitutes acceptance of the updated Terms.
          </p>
        </div>

        <div className="space-y-3 sm:space-y-4">
          <h2 className="text-xl sm:text-2xl font-semibold mt-6 sm:mt-8">10. Termination</h2>
          <p className="text-sm sm:text-base leading-relaxed">
            We may terminate or suspend your access to the Services at any time, without prior
            notice or liability, for any reason, including if you breach these Terms.
          </p>
        </div>

        <div className="space-y-3 sm:space-y-4">
          <h2 className="text-xl sm:text-2xl font-semibold mt-6 sm:mt-8">11. Severability</h2>
          <p className="text-sm sm:text-base leading-relaxed">
            If any provision of these Terms is found to be invalid or unenforceable, the remaining
            provisions shall remain in full force and effect.
          </p>
        </div>

        <div className="space-y-3 sm:space-y-4">
          <h2 className="text-xl sm:text-2xl font-semibold mt-6 sm:mt-8">12. Entire Agreement</h2>
          <p className="text-sm sm:text-base leading-relaxed">
            These Terms, together with our Privacy Policy, constitute the entire agreement between
            you and ToolboxLogic regarding the Services and supersede all prior agreements and
            understandings.
          </p>
        </div>

        <div className="space-y-3 sm:space-y-4">
          <h2 className="text-xl sm:text-2xl font-semibold mt-6 sm:mt-8">13. Contact Us</h2>
          <p className="text-sm sm:text-base leading-relaxed">
            If you have questions about these Terms, please visit our{' '}
            <a href="/contact" className="text-primary hover:underline font-medium">
              Contact Page
            </a>{' '}
            to submit an inquiry.
          </p>
          <p className="text-sm sm:text-base leading-relaxed">
            <strong>ToolboxLogic</strong>
            <br />
            Community Engagement Platform
          </p>
        </div>

        <div className="mt-8 pt-6 border-t text-xs text-muted-foreground leading-relaxed">
          <p>
            <strong>Last Updated:</strong> {displayDate}
          </p>
          <p className="mt-2">
            By using the Services, you acknowledge that you have read, understood, and agree to be
            bound by these Terms of Service.
          </p>
        </div>
      </div>
    </section>
  );
}

export default TermsOfServicePage;
